import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ApiError, type AppReleaseAdminDto } from "@finora/api-client";
import { Button, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const NAMES: Record<string, string> = {
  WINDOWS: "Windows (kasa na računaru)",
  LINUX: "Linux",
  MACOS: "Mac",
  ANDROID: "Android",
  IOS: "iPhone / iPad",
};
const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";

function Row({ r }: { r: AppReleaseAdminDto }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ version: r.version, url: r.url, notes: r.notes, mandatory: r.mandatory, minSupported: r.minSupported === "0.0.0" ? "" : r.minSupported });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => setF({ version: r.version, url: r.url, notes: r.notes, mandatory: r.mandatory, minSupported: r.minSupported === "0.0.0" ? "" : r.minSupported }), [r]);
  const fail = (e: unknown) => setMsg({ ok: false, text: e instanceof ApiError ? String(e.response.message) : "Čuvanje nije uspelo." });
  const save = useMutation({
    mutationFn: () => api.saveAppRelease(r.platform, f),
    onSuccess: () => {
      setMsg({ ok: true, text: "Sačuvano." });
      qc.invalidateQueries({ queryKey: ["app-releases"] });
    },
    onError: fail,
  });
  const del = useMutation({
    mutationFn: () => api.deleteAppRelease(r.platform),
    onSuccess: () => {
      setMsg({ ok: true, text: "Obrisano: kasa više ne prikazuje traku." });
      qc.invalidateQueries({ queryKey: ["app-releases"] });
    },
    onError: fail,
  });
  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{NAMES[r.platform] ?? r.platform}</h2>
        <span className="text-xs text-slate-400">{r.updatedAt ? `izmenjeno ${new Date(r.updatedAt).toLocaleString("sr-RS")}` : "nije podešeno"}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_1fr_140px]">
        <input className={inputCls} placeholder="Verzija (1.0.1)" value={f.version} onChange={(e) => setF({ ...f, version: e.target.value })} />
        <input className={inputCls} placeholder="Adresa za preuzimanje (https://…)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} />
        <input className={inputCls} placeholder="Najstarija (1.0.0)" value={f.minSupported} onChange={(e) => setF({ ...f, minSupported: e.target.value })} />
      </div>
      <input className={`${inputCls} mt-3`} placeholder="Šta je novo (jedna rečenica)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={f.mandatory} onChange={(e) => setF({ ...f, mandatory: e.target.checked })} />
          Obavezna nadogradnja
        </label>
        <Button size="sm" disabled={save.isPending || !f.version || !f.url} onClick={() => save.mutate()}>
          Sačuvaj
        </Button>
        {r.updatedAt && (
          <Button size="sm" variant="secondary" disabled={del.isPending} onClick={() => confirm("Obrisati podatak? Kasa više neće prikazivati traku za ovu platformu.") && del.mutate()}>
            Obriši
          </Button>
        )}
        {msg && <span className={`text-sm ${msg.ok ? "text-emerald-700" : "text-red-700"}`}>{msg.text}</span>}
      </div>
    </div>
  );
}

/** Super Admin → Verzije aplikacija: koju verziju kase nudimo po platformi (kasa pita preko GET /api/app/latest). */
export function AppReleasesAdminPage() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["app-releases"], queryFn: () => api.listAppReleases() });
  return (
    <div className="space-y-4">
      <button onClick={() => navigate("/finora-admin")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Nazad na HAFiskal Admin
      </button>
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Verzije aplikacija</h1>
        <p className="text-sm text-slate-500">
          Ovde upisujete koja je najnovija verzija kase. Kasa sama proverava i korisniku prikaže traku „Dostupna je nova verzija“.
          „Najstarija“ je najstarija verzija koja još sme da radi; starija od nje dobija obaveznu nadogradnju. Prazno polje znači da nema ograničenja.
        </p>
      </div>
      {q.isLoading ? <Skeleton className="h-32 w-full" /> : (q.data ?? []).map((r) => <Row key={r.platform} r={r} />)}
    </div>
  );
}
